"use strict";
/**
 * Fired when the ESC key is pressed.
 * @event Autodesk.Viewing#ESCAPE_EVENT
 */
export const ESCAPE_EVENT = 'escape';
/**
 * Fired repeatedly throughout the process of opening a model/drawing.
 * @event Autodesk.Viewing#PROGRESS_UPDATE_EVENT
 * @property {number} percent - Estimated progress.
 * @property {number} state - Value from Autodesk.Viewing.ProgressState, providing details on the progress state.
 * @property {object} model - Model being loaded.
 * @property {number} [loaded] - Bytes downloaded so far (only set during ProgressState.DOWNLOADING).
 * @property {number} [total] - Total bytes to download, when known (only set during ProgressState.DOWNLOADING).
 */
export const PROGRESS_UPDATE_EVENT = 'progress';
/**
 * Fired when the screen mode changes.
 * @event Autodesk.Viewing#FULLSCREEN_MODE_EVENT
 * @property {Autodesk.Viewing.ScreenMode} mode - New screen mode.
 */
export const FULLSCREEN_MODE_EVENT = 'fullScreenMode';
/**
 * Fired then the navigation tool changes.
 * @event Autodesk.Viewing#NAVIGATION_MODE_CHANGED_EVENT
 * @property {string} id - Tool identifier.
 */
export const NAVIGATION_MODE_CHANGED_EVENT = 'navmode';
/**
 * Fired when the viewer state is restored.
 * @event Autodesk.Viewing#VIEWER_STATE_RESTORED_EVENT
 * @property {boolean} value - Success of the state restoration.
 */
export const VIEWER_STATE_RESTORED_EVENT = 'viewerStateRestored';
/**
 * Fired when the viewer size changes.
 * @event Autodesk.Viewing#VIEWER_RESIZE_EVENT
 * @property {number} width - New width of the viewer.
 * @property {number} height - New height of the viewer.
 */
export const VIEWER_RESIZE_EVENT = 'viewerResize';
/**
 * Fired when the viewer is fully initialized.
 * @event Autodesk.Viewing#VIEWER_INITIALIZED
 */
export const VIEWER_INITIALIZED = 'viewerInitialized';
/**
 * Fired when the viewer is fully uninitialized.
 * @event Autodesk.Viewing#VIEWER_UNINITIALIZED
 */
export const VIEWER_UNINITIALIZED = 'viewerUninitialized';

/**
 * Fired when the viewer's language/locale is changed.
 * @event Autodesk.Viewing#LANGUAGE_CHANGED_EVENT
 * @property {string} language - The new language code (e.g., 'en', 'de', 'ja').
 */
export const LANGUAGE_CHANGED_EVENT = 'languageChanged';

/**
 * Fired when the viewer begins loading a model, before the
 * Model instance gets created.
 *
 * @event Autodesk.Viewing#LOADER_LOAD_FILE_EVENT
 * @property {object} loader - Instance of the Loader class
 */
export const LOADER_LOAD_FILE_EVENT = 'loaderLoadFile';

/**
 * Fired when an error is detected during a load.
 *
 * @event Autodesk.Viewing#LOADER_LOAD_ERROR_EVENT
 * @property {object} loader - Instance of the Loader class
 * @property {object} error - The error reported by the loader.
 */
export const LOADER_LOAD_ERROR_EVENT = 'loaderLoadError';

/**
 * Fired when a loader thinks its time to repaint the model while loading
 *
 * @event Autodesk.Viewing#LOADER_REPAINT_REQUEST_EVENT
 * @property {Object} loader - Instance of the Loader class
 * @property {Model} model - Loaded model
 */
export const LOADER_REPAINT_REQUEST_EVENT = 'loaderRepaint';

/**
 * Fired when PDF text strings have been extracted and stored on the model data.
 * @event Autodesk.Viewing#PDF_STRINGS_EXTRACTED
 * @property {object} model - Model data.
 * @private
 */
export const PDF_STRINGS_EXTRACTED = 'pdfStringsExtracted';

/**
 * Fired when the viewer receives and parses the initial model manifest.
 * @event Autodesk.Viewing#MODEL_ROOT_LOADED_EVENT
 * @property {object} svf - Parsed SVF/F2D JSON.
 * @property {object} model - Model data.
 */
export const MODEL_ROOT_LOADED_EVENT = 'svfLoaded';

/**
 * Fired when the model/drawing finishes loading.
 * @event Autodesk.Viewing#GEOMETRY_LOADED_EVENT
 * @property {object} model - Model data.
 */
export const GEOMETRY_LOADED_EVENT = 'geometryLoaded';

/**
* Fired when the model/drawing textures finish loading.
* @event Autodesk.Viewing#TEXTURES_LOADED_EVENT
* @property {object} model - Model data.
*/
export const TEXTURES_LOADED_EVENT = 'texturesLoaded';
/**
 * Fired when the object tree is successfully created.
 * @event Autodesk.Viewing#OBJECT_TREE_CREATED_EVENT
 * @property {object} svf - Parsed SVF/F2D JSON.
 * @property {object} model - Model data.
 */
export const OBJECT_TREE_CREATED_EVENT = 'propertyDbLoaded';
/**
 * Fired when the viewpoint tree is updated. Mainly used for loading a ViewpointTreeRoot from a Packfile (Navisworks).
 * @event Autodesk.Viewing#VIEWPOINT_TREE_UPDATED_EVENT
 * @property {object} model - Model data.
 */
export const VIEWPOINT_TREE_UPDATED_EVENT = 'viewpointTreeUpdated';
/**
 * Fired when there's an error while parsing the object tree.
 * @event Autodesk.Viewing#OBJECT_TREE_UNAVAILABLE_EVENT
 * @property {object} svf - Parsed SVF/F2D JSON.
 * @property {object} model - Model data.
 */
export const OBJECT_TREE_UNAVAILABLE_EVENT = 'propertyDbUnavailable';
/**
 * Fired when there's a progress event during propertyDB loading.
 * @event Autodesk.Viewing#OBJECT_TREE_LOAD_PROGRESS_EVENT
 * @property {object} svf - Parsed SVF/F2D JSON.
 * @property {object} model - Model data.
 */
export const OBJECT_TREE_LOAD_PROGRESS_EVENT = 'propertyDbLoadProgress';

/**
 * Fired when a model is removed from the viewer.
 * Note that this event fires _after_ destructor. If
 * you need to access the model, e.g. via model.getData(), use
 * BEFORE_MODEL_UNLOADED instead.
 * @event Autodesk.Viewing#MODEL_UNLOADED_EVENT
 * @property {object} model - Model data (after disposing data)
 */
export const MODEL_UNLOADED_EVENT = 'modelUnloaded';

/**
 * Fired if a model unload is about to happen, but the model is still fully available.
 * @event Autodesk.Viewing#BEFORE_MODEL_UNLOAD_EVENT
 * @property {object} model - Model data.
 */
export const BEFORE_MODEL_UNLOAD_EVENT = 'beforeModelUnload';

/**
 * Fired when a model is added to model queue so as to be visible.
 * @event Autodesk.Viewing#MODEL_ADDED_EVENT
 * @property {object} model - Model data.
 */
export const MODEL_ADDED_EVENT = 'modelAdded';

/**
 * Fired when a model is removed from model queue, i.e. changed to invisible.
 * @event Autodesk.Viewing#MODEL_REMOVED_EVENT
 * @property {object} model - Model data.
 */
export const MODEL_REMOVED_EVENT = 'modelRemoved';

/**
 * Fired when the layers of the model are successfully loaded.
 * @event Autodesk.Viewing#MODEL_LAYERS_LOADED_EVENT
 * @property {object} root - Model layers root.
 * @property {object} model - Model data.
 */
export const MODEL_LAYERS_LOADED_EVENT = 'modelLayersLoaded';

/**
 * Fired a when model transform matrix has been changed. This usually happens if either placement or globalOffset
 * has changed for a model.
 * @event Autodesk.Viewing#MODEL_TRANSFORM_CHANGED_EVENT
 * @property {object} model - Model data.
 * @property {object} matrix - Transform matrix.
 */
export const MODEL_TRANSFORM_CHANGED_EVENT = 'modelTransformChanged';

/**
 * Fired if placementTransform was changed.
 */
export const MODEL_PLACEMENT_CHANGED_EVENT = 'placementTransformChanged';

/**
 * Fired if viewportBounds was changed.
 */
export const MODEL_VIEWPORT_BOUNDS_CHANGED_EVENT = 'viewportBoundsChanged';

/**
 * Fired if fragment bounding boxes are explicitly set on the model.
 */
export const MODEL_FRAGMENT_BOUNDING_BOXES_SET_EVENT = 'fragmentBoundingBoxesSet';

/**
 * Fired if a new traversal controller is added to the model.
 */
export const MODEL_TRAVERSAL_CONTROLLER_ADDED = 'traversalControllerAdded';

/**
 * Fired before a viewer extension is loaded.
 * @event Autodesk.Viewing#EXTENSION_PRE_LOADED_EVENT
 * @property {string} extensionId - Extension identifier.
 */
export const EXTENSION_PRE_LOADED_EVENT = 'extensionPreLoaded';
/**
 * Fired when a viewer extension is successfully loaded.
 * @event Autodesk.Viewing#EXTENSION_LOADED_EVENT
 * @property {string} extensionId - Extension identifier.
 */
export const EXTENSION_LOADED_EVENT = 'extensionLoaded';
/**
 * Fired before a viewer extension is unloaded.
 * @event Autodesk.Viewing#EXTENSION_PRE_UNLOADED_EVENT
 * @property {string} extensionId - Extension identifier.
 */
export const EXTENSION_PRE_UNLOADED_EVENT = 'extensionPreUnloaded';
/**
 * Fired when a viewer extension is successfully unloaded.
 * @event Autodesk.Viewing#EXTENSION_UNLOADED_EVENT
 * @property {string} extensionId - Extension identifier.
 */
export const EXTENSION_UNLOADED_EVENT = 'extensionUnloaded';
/**
 * Fired before a viewer extension is activated.
 * @event Autodesk.Viewing#EXTENSION_PRE_ACTIVATED_EVENT
 * @property {string} extensionId - Extension identifier.
 * @property {string} mode - Activated mode.
 */
export const EXTENSION_PRE_ACTIVATED_EVENT = 'extensionPreActivated';
/**
 * Fired after a viewer extension is activated.
 * @event Autodesk.Viewing#EXTENSION_ACTIVATED_EVENT
 * @property {string} extensionId - Extension identifier.
 * @property {string} mode - Activated mode.
 */
export const EXTENSION_ACTIVATED_EVENT = 'extensionActivated';
/**
 * Fired before a viewer extension is deactivated.
 * @event Autodesk.Viewing#EXTENSION_PRE_DEACTIVATED_EVENT
 * @property {string} extensionId - Extension identifier.
 */
export const EXTENSION_PRE_DEACTIVATED_EVENT = 'extensionPreDeactivated';
/**
 * Fired after a viewer extension is deactivated.
 * @event Autodesk.Viewing#EXTENSION_DEACTIVATED_EVENT
 * @property {string} extensionId - Extension identifier.
 */
export const EXTENSION_DEACTIVATED_EVENT = 'extensionDeactivated';
/**
 * Fired when the list of selected objects changes.
 * @event Autodesk.Viewing#SELECTION_CHANGED_EVENT
 * @property {number[]} fragIdsArray - Fragment IDs of selected objects.
 * @property {number[]} dbIdArray - dbIDs of selected objects.
 * @property {number[]} nodeArray - Same as dbIdArray.
 * @property {object} model - Model data.
 */
export const SELECTION_CHANGED_EVENT = 'selection';
/**
 * Fired when the list of selected objects changes in a multi-model context.
 * @event Autodesk.Viewing#AGGREGATE_SELECTION_CHANGED_EVENT
 * @property {object[]} selections - List of objects containing the typical selection properties
 *   of {@link Autodesk.Viewing#SELECTION_CHANGED_EVENT} for each model.
 */
export const AGGREGATE_SELECTION_CHANGED_EVENT = 'aggregateSelection';
/**
 * Fired when the viewer isolates a set of objects (i.e., makes everything else invisible or ghosted).
 * @event Autodesk.Viewing#ISOLATE_EVENT
 * @property {number[]} nodeIdArray - List of isolated node IDs.
 * @property {object} model - Model data.
 */
export const ISOLATE_EVENT = 'isolate';
/**
 * Fired when the list of isolated objects changes in a multi-model context.
 * @event Autodesk.Viewing#AGGREGATE_ISOLATION_CHANGED_EVENT
 * @property {object[]} isolation - List of objects containing the typical selection properties
 *   of {@link Autodesk.Viewing#ISOLATE_EVENT} for each model.
 */
export const AGGREGATE_ISOLATION_CHANGED_EVENT = 'aggregateIsolation';
/**
 * Fired when the viewer hides a set of objects.
 * @event Autodesk.Viewing#HIDE_EVENT
 * @property {number[]} nodeIdArray - List of hidden node IDs.
 * @property {object} model - Model data.
 */
export const HIDE_EVENT = 'hide';
/**
 * Fired when the list of hidden objects changes in a multi-model context.
 * @event Autodesk.Viewing#AGGREGATE_HIDDEN_CHANGED_EVENT
 * @property {object[]} hidden - List of objects containing the typical selection properties
 *   of {@link Autodesk.Viewing#HIDE_EVENT} for each model.
 */
export const AGGREGATE_HIDDEN_CHANGED_EVENT = 'aggregateHidden';

/**
 * Fired when the viewer shows a set of objects.
 * @event Autodesk.Viewing#SHOW_EVENT
 * @property {number[]} nodeIdArray - List of shown node IDs.
 * @property {object} model - Model data.
 */
export const SHOW_EVENT = 'show';

/**
 * Fired to show the properties of the object.
 * @event Autodesk.Viewing#SHOW_PROPERTIES_EVENT
 * @property {number} dbId - dbId of the object.
 * @property {object} model - Model data.
 */
export const SHOW_PROPERTIES_EVENT = 'showProperties';

/**
 * Fired whenever `viewer.showAll()` is used.
 *
 * @event Autodesk.Viewing#SHOW_ALL_EVENT
 */
export const SHOW_ALL_EVENT = 'showAll';

/**
* Fired whenever `viewer.hideAll()` is used.
*
* @event Autodesk.Viewing#HIDE_ALL_EVENT
*/
export const HIDE_ALL_EVENT = 'hideAll';

/**
 * Fired when a camera changes.
 * @event Autodesk.Viewing#CAMERA_CHANGE_EVENT
 * @property {object} camera - Affected camera.
 */
export const CAMERA_CHANGE_EVENT = 'cameraChanged';
/**
 * Fired whenever the Explode tool is used.
 * @event Autodesk.Viewing#EXPLODE_CHANGE_EVENT
 * @property {number} scale - Scale of the current exploded state.
 * @property {object} options - Optional values of the current exploded state.
 */
export const EXPLODE_CHANGE_EVENT = 'explodeChanged';
/**
 * Fired when a ``fitToView`` operation is applied.
 * @event Autodesk.Viewing#FIT_TO_VIEW_EVENT
 * @property {boolean} immediate - True if the change was immediate.
 * @property {number[]} nodeIdArray - List of node IDs fitted. Array is empty when fitting to the whole model.
 * @property {object} model - Model data.
 */
export const FIT_TO_VIEW_EVENT = 'fitToView';
/**
 * Fired when ``fitToView`` operation is applied, supports multi-model contexts.
 * @event Autodesk.Viewing#AGGREGATE_FIT_TO_VIEW_EVENT
 * @property {object[]} selection - List of objects each containing a ``model`` instance and a ``selection`` array of ids.
 */
export const AGGREGATE_FIT_TO_VIEW_EVENT = 'aggregateFitToView';
/**
 * Fired when the cutting planes change.
 * @event Autodesk.Viewing#CUTPLANES_CHANGE_EVENT
 * @property {object[]} planes - List of cutplanes.
 */
export const CUTPLANES_CHANGE_EVENT = 'cutplanesChanged';
/**
 * Fired when a tool is activated or deactivated.
 * @event Autodesk.Viewing#TOOL_CHANGE_EVENT
 * @property {string} toolName - Name of a specific mode of a tool.
 * @property {object} tool - Tool object.
 * @property {boolean} active - Current status of the tool.
 */
export const TOOL_CHANGE_EVENT = 'toolChanged';
/**
 * Fired when rendering options change.
 * @event Autodesk.Viewing#RENDER_OPTION_CHANGED_EVENT
 */
export const RENDER_OPTION_CHANGED_EVENT = 'renderOptionChanged';
/**
 * Fired when the render frame shown by the Viewer is final or complete (it has
 * no more pending geometry or post processing effects which delay incoming frames),
 * or when the Viewer stops showing final frames. The name refers to when the
 * state changes from busy to idle for the renderer, or vice versa. To know
 * when all geometry is fully displayed, also check for GEOMETRY_LOADED_EVENT.
 *
 * @event Autodesk.Viewing#FINAL_FRAME_RENDERED_CHANGED_EVENT
 * @property {boolean} finalFrame - final frame is displayed this tick.
 */
export const FINAL_FRAME_RENDERED_CHANGED_EVENT = 'finalFrameRenderedChanged';

/**
 * This event is triggered for each tick that performs some form of rendering work, providing information about the
 * frame sequence and rendering completion status.
 *
 * Note: A series of intermediate frames is not guaranteed to end with isMainSceneDone = true
 *
 * @event Autodesk.Viewing#RENDERING_TICKED_EVENT
 * @property {object} value - Event data object containing frame rendering information.
 * @property {number} value.renderTicksSinceClear - Number of ticks that render something since the last clear.
 * @property {boolean} value.isMainSceneDone - True when rendering of the main scene is done. Note that updating
 *     overlays emit events without clearing the main scene, increasing the tick counter.
 *
 * @example
 * // Progressive rendering sequence (3 intermediate frames + 1 additional overlay composite):
 * // { renderTicksSinceClear: 0, isMainSceneDone: false } // tick where the main scene was cleared
 * // { renderTicksSinceClear: 1, isMainSceneDone: false } // tick rendering an intermediate frame, still converging
 * // { renderTicksSinceClear: 2, isMainSceneDone: true  } // tick wrapping up the main scene rendering
 * // { renderTicksSinceClear: 3, isMainSceneDone: true  } // tick updating just the overlays
 */
export const RENDERING_TICKED_EVENT = 'renderingTicked';

/**
 * Fired when the render has presented to the screen.
 * @event Autodesk.Viewing#RENDER_PRESENTED_EVENT
 */
export const RENDER_PRESENTED_EVENT = 'renderPresented';
/**
 * Fired when visibility of a 2D layer changes.
 * @event Autodesk.Viewing#LAYER_VISIBILITY_CHANGED_EVENT
 */
export const LAYER_VISIBILITY_CHANGED_EVENT = 'layerVisibility';

/**
 * Fired when a user preference property changes.
 * @event Autodesk.Viewing#PREF_CHANGED_EVENT
 * @property {string} name - Property name.
 * @property {object} value - New property value.
 */
export const PREF_CHANGED_EVENT = 'PrefChanged';
/**
 * Fired when a user preference property is reset.
 * @event Autodesk.Viewing#PREF_RESET_EVENT
 * @property {string} name - Property name.
 * @property {object} value - New property value.
 */
export const PREF_RESET_EVENT = 'PrefReset';

/**
 * Fired as a result of invoking `viewer.restoreDefaultSettings()` to restore default settings.
 * Will get fired after all other Autodesk.Viewing.PREF_CHANGED_EVENT get fired.
 * @event Autodesk.Viewing#RESTORE_DEFAULT_SETTINGS_EVENT
 */
export const RESTORE_DEFAULT_SETTINGS_EVENT = 'restoreDefaultSettings';

/**
 * Fired when animations are successfully initialized.
 * @event Autodesk.Viewing#ANIMATION_READY_EVENT
 */
export const ANIMATION_READY_EVENT = 'animationReady';

/**
 * Fired whenever a camera transition is finished, such as Focus, Go to Home View,
 * Restore State, restore Named Views, and others.
 * @event Autodesk.Viewing#CAMERA_TRANSITION_COMPLETED
 */
export const CAMERA_TRANSITION_COMPLETED = 'cameraTransitionCompleted';

/**
 * Fired when user clicks on a hyperlink embedded in the model.
 * @event Autodesk.Viewing#HYPERLINK_EVENT
 * @property {object} data - Hyperlink data.
 */
export const HYPERLINK_EVENT = 'hyperlink';

export const HYPERLINK_NAVIGATE = 'hyperlink_navigate';

export const LOAD_GEOMETRY_EVENT = 'load_geometry';

/**
* Fired when something in the view changes that may expose missing geometry.
* @event Autodesk.Viewing#LOAD_MISSING_GEOMETRY
* @property {boolean} [delay] - A flag used to aggregate multiple events during user interactions.
*                               Defaults to true.
*/
export const LOAD_MISSING_GEOMETRY = 'loadMissingGeometry';

/**
 * Fired when the WebGL context or WebGPU device is lost.
 * @event Autodesk.Viewing#GPU_CONTEXT_LOST_EVENT
 */
export const GPU_CONTEXT_LOST_EVENT = 'gpuContextLost';

/**
 * Deprecated. Use GPU_CONTEXT_LOST_EVENT instead.
 * @event Autodesk.Viewing#WEBGL_CONTEXT_LOST_EVENT
 */
export const WEBGL_CONTEXT_LOST_EVENT = 'gpuContextLost';

/**
 * Fired when the WebGLRenderingContext was restored
 *
 * @event Autodesk.Viewing#WEBGL_CONTEXT_RESTORED_EVENT
 */
export const WEBGL_CONTEXT_RESTORED_EVENT = 'webglcontextrestored';

/**
 * Fired when a leaflet screenshot needs to be canceled before downloading all required / pending tiles.
 *
 * @event Autodesk.Viewing#CANCEL_LEAFLET_SCREENSHOT
 */
export const CANCEL_LEAFLET_SCREENSHOT = 'cancelLeafletScreenshot';


/**
 * Fired after a successful call into {Autodesk.Viewing.Viewer3D#setView}.
 *
 * @event Autodesk.Viewing#SET_VIEW_EVENT
 * @property {Autodesk.Viewing.BubbleNode} view - The view object that was applied.
 */
export const SET_VIEW_EVENT = 'setView';


/**
 * Fired when the first pixel of the model is ready to be rendered.
 *
 * @event Autodesk.Viewing#RENDER_FIRST_PIXEL
 */
export const RENDER_FIRST_PIXEL = 'renderFirstPixel';

/**
 * Fired when new geometry has been added to the scene
 *
 * @event Autodesk.Viewing#NEW_GEOMETRY_ADDED
 */
export const NEW_GEOMETRY_ADDED = 'newGeometryAdded';

/**
 * Fired when a new material has been added to the scene
 *
 * @event Autodesk.Viewing#NEW_MATERIAL_ADDED
 */
export const NEW_MATERIAL_ADDED = 'newMaterialAdded';
/**
 * Fired when a profile is added to the viewer.profileManager
 * @event Autodesk.Viewing#PROFILE_CHANGE_EVENT
 */
export const PROFILE_CHANGE_EVENT = 'profileChanged';

/**
 * Fired when a scene part is about to be rendered
 * @event Autodesk.Viewing#PROFILE_CHANGE_EVENT
 */
export const RENDER_SCENE_PART = 'renderScenePart';

/**
 * Fired when object under mouse changed.
 * @event Autodesk.Viewing#OBJECT_UNDER_MOUSE_CHANGED
 */
export const OBJECT_UNDER_MOUSE_CHANGED = 'hoverObjectChanged';

/**
 * Fired when AnimController calls Autodesk.Viewing.Private.fadeValue
 * @event Autodesk.Viewing#ANIM_ENDED
 */
export const ANIM_ENDED = 'animEnded';

/** Fired when transition started
 * @event Autodesk.Viewing#TRANSITION_STARTED
 * @property {object} sceneAnimState // scene state of transition
 */
export const TRANSITION_STARTED = 'transitionStarted';

/** Fired when transition ended
 * @event Autodesk.Viewing#TRANSITION_ENDED
 * @property {object} sceneAnimState // scene state of transition, this will be null for models that do not have any clusters or animations
 */
export const TRANSITION_ENDED = 'transitionEnded';

/**
 * Fired when fragments are loaded after the initial model load.
 * @event Autodesk.Viewing#FRAGMENTS_LOADED_EVENT
 * @property {object} model - Model data.
 */
export const FRAGMENTS_LOADED_EVENT = 'fragmentsLoaded';

/**
 * Fired when fragments are unloaded.
 * @event Autodesk.Viewing#FRAGMENTS_UNLOADED_EVENT
 * @property {object} model - Model data.
 */
export const FRAGMENTS_UNLOADED_EVENT = 'fragmentsUnloaded';

/**
 * Fired when scene is updated
 * @event Autodesk.Viewing#SCENE_UPDATED_EVENT
 * @property {object} model - Model data.
 */
export const SCENE_UPDATED_EVENT = 'sceneUpdated';

/**
 * Fired when the viewer triggers a refresh of the custom properties/attributes from PropertyDb.
 * @event Autodesk.Viewing#REFRESH_CUSTOM_PROPERTIES
 * @property {object} model - Model data.
 * @property {boolean} [defsChanged] - True when attribute definitions changed compared to the previous refresh.
 * @property {string[]} [changedKeys] - Property-hash keys of added, modified, or removed definitions.
 *   Only present on subsequent refreshes (not on the initial load).
 * @private
 */
export const REFRESH_CUSTOM_PROPERTIES = 'refreshCustomProperties';


/**
 * Event for new custom properties/attributes value from PropertyDb.
 * @event Autodesk.Viewing#REFRESH_CUSTOM_PROPERTIES_VALUES
 * @property {object} model - Model data.
 * @property {boolean} [defsChanged] - True when attribute definitions were re-fetched and changed.
 * @property {string[]} [changedKeys] - Property-hash keys of added, modified, or removed definitions.
 *   Only present when definitions were re-fetched on a subsequent refresh.
 * @property {boolean} [valsChanged] - True when any attribute values changed compared to the previous refresh.
 * @property {string[]} [changedValKeys] - Property-hash keys of attributes whose values changed.
 *   Only present on subsequent refreshes (not on the initial load). Change detection uses an
 *   additive commutative hash; false-negative probability is ~1/2^32 per attribute per refresh.
 * @private
 */
export const REFRESH_CUSTOM_PROPERTIES_VALUES = 'refreshCustomPropertiesValues';

/**
 * Fired during custom properties workflows to report loading progress.
 * Emitted at the start, on intermediate steps, and at the end of each workflow. When the workflow
 * function rejects, `'error'` is emitted first, then `'end'` with `workflowFailed: true`.
 *
 * @event Autodesk.Viewing#CUSTOM_PROPERTIES_PROGRESS
 * @property {string} name - Workflow name. E.g. `'loadModels'`, `'applyBindings'`, ...
 * @property {string} workflowId - Unique identifier for this workflow run (e.g. `'loadModels:1'`).
 * @property {string} phase - Lifecycle phase of the workflow. Automatically emitted phases are
 *   `'start'`, `'end'`, and `'error'`. On rejection, both `'error'` and `'end'` fire (the latter
 *   includes `workflowFailed: true`). Intermediate phases reported during execution include
 *   `'progress'` (per-model step completed), `'bindings'` (all definition fetches done during
 *   initial load), and `'values'` (a model's values fetched during initial load).
 * @property {string} [requestId] - Caller-supplied request ID, present when the originating call
 *   provided one.
 * @property {number} eligibleModels - Total number of eligible (OTG/SVF2) models in the workflow.
 * @property {string[]} uniqueModelUrns - Deduplicated seed file URNs being tracked.
 * @property {number} bindingsCompleted - Number of unique URNs whose bindings have finished loading.
 * @property {number} valuesCompleted - Number of unique URNs whose values have finished loading.
 * @property {number} bindingsErrors - Number of unique URNs whose bindings loading ended in error.
 * @property {number} valuesErrors - Number of unique URNs whose values loading ended in error.
 * @property {object} [error] - The error object, present only when `phase` is `'error'`.
 * @property {boolean} [workflowFailed] - True when `phase` is `'end'` after a rejected workflow.
 * @private
 */
export const CUSTOM_PROPERTIES_PROGRESS = 'customPropertiesProgress';


/**
 * Fired when the viewers visibility has changed (i.e. a viewer window
 * got shown or hidden).
 *
 * NOTE: This event relies on the checkVisibility method being available on DOM elements in the browser.
 *       If this feature is missing, the event will not be fired.
 *       This should be the case in all up-to-date browsers. Most browsers have added it in 2022,
 *       but Safari only at the beginning of 2024.
 *
 * @event Autodesk.Viewing#VIEWER_VISIBILITY_CHANGED
 * @property {boolean} visible - True if the viewer is visible, false otherwise.
 */
export const VIEWER_VISIBILITY_CHANGED = 'viewerVisibilityChanged';

/**
 * Fired each rendering tick regardless of whether the viewer actually needs to render.
 * This event is intended for updates that need to be in sync with the render loop.
 *
 * Example usage:
 * ```javascript
 * this.viewer.addEventListener(VIEWER_RENDER_TICK, event) => {
 *      // do some update that needs to be in sync with the render loop
 * });
 * ```
 * @event Autodesk.Viewing#VIEWER_RENDER_TICK
 */
export const VIEWER_RENDER_TICK = "renderTick";

/**
 * Fired when the viewer detects that the GPU may be underperforming (e.g. integrated GPU, software renderer).
 * @event Autodesk.Viewing#SLOW_GPU_WARNING_EVENT
 * @property {'integrated-gpu'|'software-renderer'} reason - Detected reason for the warning.
 * @property {{ vendor: string, renderer: string }} rendererInfo - Raw WebGL vendor/renderer strings.
 * @property {function} suppressDefault - Call to prevent the default HudMessage from being shown.
 */
export const SLOW_GPU_WARNING_EVENT = 'slowGpuWarning';